import { getIframeStyles, getSpinnerHtml } from './styles.js';
import { sendInit, onIframeMessage } from './postmessage.js';
import type { CalAttrs, IframeToParent } from './types.js';

export interface CreateIframeOptions {
  src: string;
  calOrigin: string;
  attrs: CalAttrs;
  /** Called when the iframe sends cal:resize */
  onResize?: (height: number) => void;
  /** Called when booking is completed */
  onBooked?: (bookingId: string) => void;
  /** Called when the iframe requests close */
  onClose?: () => void;
}

export interface IframeHandle {
  el: HTMLIFrameElement;
  wrapper: HTMLDivElement;
  /** Remove event listener and destroy the iframe */
  destroy: () => void;
}

/**
 * Create a sandboxed iframe pointing at a cal booking page.
 *
 * The iframe:
 *   - Uses a safe `sandbox` attribute
 *   - Listens for postMessage events from the embedded landing
 *   - Sends cal:init once loaded (theme / prefill / locale)
 *   - Shows a spinner while loading
 */
export function createIframe(opts: CreateIframeOptions): IframeHandle {
  const { src, calOrigin, attrs, onResize, onBooked, onClose } = opts;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:relative;width:100%;height:100%;overflow:hidden';

  // Loading spinner (removed on iframe load)
  const spinnerColor = attrs.color ?? '#1A365D';
  wrapper.innerHTML = getSpinnerHtml(spinnerColor);

  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.title = 'Booking calendar';
  iframe.setAttribute('aria-label', 'Booking calendar');
  iframe.setAttribute('loading', 'eager');
  iframe.setAttribute('allowfullscreen', '');
  // Safe sandbox: scripts + forms + same-origin (for auth/session) + top nav by user action
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-same-origin allow-top-navigation-by-user-activation allow-popups',
  );
  iframe.style.cssText = [
    getIframeStyles(),
    'position:absolute;inset:0;opacity:0;transition:opacity 200ms ease',
  ].join(';');

  // Remove spinner + fade in iframe once loaded
  iframe.addEventListener('load', () => {
    // Remove spinner element (first child)
    const spinner = wrapper.querySelector('div');
    if (spinner) spinner.remove();
    iframe.style.opacity = '1';

    // Send init message
    sendInit(iframe, calOrigin, {
      theme: attrs.theme,
      prefill:
        attrs.prefillEmail || attrs.prefillName
          ? { email: attrs.prefillEmail, name: attrs.prefillName }
          : undefined,
      locale: attrs.locale,
    });
  });

  wrapper.appendChild(iframe);

  // Listen for messages from the embedded landing
  const unsubscribe = onIframeMessage(calOrigin, (msg: IframeToParent) => {
    switch (msg.type) {
      case 'cal:resize':
        onResize?.(msg.height);
        break;
      case 'cal:booked':
        onBooked?.(msg.bookingId);
        break;
      case 'cal:close':
        onClose?.();
        break;
    }
  });

  return {
    el: iframe,
    wrapper,
    destroy() {
      unsubscribe();
      wrapper.remove();
    },
  };
}
